import { useState, useEffect, useCallback, useRef } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Heart, MessageCircle, Edit, Trash2, Pin, Reply, X, ChevronDown, ChevronUp } from 'lucide-react';
import { pb, InspireComment } from '@/lib/pocketbase';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { detectTextDirection } from '@/utils/textDirection';
import { linkifyWithBreaks } from '@/utils/linkify';

interface CommentItemProps {
  comment: InspireComment;
  userId?: string;
  postOwnerId?: string;
  onDelete: () => void;
  onEdit: () => void;
  onAuthRequired?: () => void;
  level?: number; // For nested replies (0 = top level, 1 = reply, etc.)
}

export const CommentItem = ({
  comment,
  userId,
  postOwnerId,
  onDelete,
  onEdit,
  onAuthRequired,
  level = 0
}: CommentItemProps) => {
  const navigate = useNavigate();
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(comment.likes_count || 0);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const [replies, setReplies] = useState<InspireComment[]>([]);
  const [showReplies, setShowReplies] = useState(false);
  const [repliesCount, setRepliesCount] = useState(0);
  const [isLoadingReplies, setIsLoadingReplies] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const repliesContainerRef = useRef<HTMLDivElement>(null);

  const commenter = comment.expand?.user_id;
  const tutorRecord = (comment as any).tutorRecord;

  // Get avatar - prioritize tutor profile picture
  let avatarUrl = '';
  if (tutorRecord) {
    if (tutorRecord.profilePictureUrl) {
      avatarUrl = tutorRecord.profilePictureUrl;
    } else if (tutorRecord.profilePicture) {
      try {
        avatarUrl = pb.files.getURL(tutorRecord, tutorRecord.profilePicture);
      } catch (error) {
        console.error('Error getting file URL:', error);
      }
    }
  }
  
  // Fallback to user avatar if no tutor avatar
  if (!avatarUrl && commenter?.avatar) {
    avatarUrl = commenter.avatar;
  }

  // Check if user has liked this comment
  useEffect(() => {
    const checkLikeStatus = async () => {
      if (!userId) return;
      
      try {
        await pb.collection('inspire_comment_likes').getFirstListItem(
          `comment_id="${comment.id}" && user_id="${userId}"`
        );
        setIsLiked(true);
      } catch {
        setIsLiked(false);
      }
    };

    checkLikeStatus();
  }, [comment.id, userId]);

  // Fetch replies on mount for top-level comments (prefetch for instant display)
  useEffect(() => {
    if (level === 0) {
      fetchReplies();
    }
  }, [level, comment.id, refreshKey]);

  const fetchReplies = useCallback(async () => {
    setIsLoadingReplies(true);
    try {
      const records = await pb.collection('inspire_comments').getFullList({
        filter: `parent_comment_id="${comment.id}"`,
        sort: 'created',
        expand: 'user_id'
      });

      // For each reply, check if the user is a tutor
      const repliesWithTutorInfo = await Promise.all(
        records.map(async (reply) => {
          try {
            const tutorRecords = await pb.collection('tutors').getFullList({
              filter: `user="${reply.user_id}"`,
              limit: 1
            });
            
            if (tutorRecords.length > 0) {
              return { ...reply, tutorRecord: tutorRecords[0] };
            }
            return reply;
          } catch (error) {
            return reply;
          }
        })
      );

      setReplies(repliesWithTutorInfo as InspireComment[]);
      setRepliesCount(repliesWithTutorInfo.length);
    } catch (error) {
      console.error('Error fetching replies:', error);
      setRepliesCount(0);
    } finally {
      setIsLoadingReplies(false);
    }
  }, [comment.id, refreshKey]);

  const toggleReplies = () => {
    setShowReplies(!showReplies);
    
    // Scroll to replies when expanding
    if (!showReplies && repliesCount > 0) {
      setTimeout(() => {
        const element = repliesContainerRef.current;
        if (!element) return;
        
        // Find the ScrollArea viewport (Radix UI scroll container)
        const scrollContainer = element.closest('[data-radix-scroll-area-viewport]');
        
        if (scrollContainer) {
          // Get element position relative to scroll container
          const elementRect = element.getBoundingClientRect();
          const containerRect = scrollContainer.getBoundingClientRect();
          const scrollTop = scrollContainer.scrollTop;
          
          // Calculate target scroll position
          const targetScroll = scrollTop + (elementRect.top - containerRect.top) - 20; // 20px padding
          
          // Smooth scroll to the replies
          scrollContainer.scrollTo({
            top: targetScroll,
            behavior: 'smooth'
          });
        } else {
          // Fallback to standard scrollIntoView if not in ScrollArea
          element.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'nearest' 
          });
        }
      }, 300); // Longer delay to ensure replies are fully rendered
    }
  };

  const handleLike = async () => {
    if (!userId) {
      onAuthRequired?.();
      return;
    }

    try {
      if (isLiked) {
        // Unlike
        const existingLike = await pb.collection('inspire_comment_likes').getFirstListItem(
          `comment_id="${comment.id}" && user_id="${userId}"`
        );
        await pb.collection('inspire_comment_likes').delete(existingLike.id);
        setIsLiked(false);
        setLikesCount(prev => Math.max(0, prev - 1));
      } else {
        // Like
        await pb.collection('inspire_comment_likes').create({
          comment_id: comment.id,
          user_id: userId
        });
        setIsLiked(true);
        setLikesCount(prev => prev + 1);
        
        // Send notification to comment author
        try {
          console.log('🔔 Checking comment like notification:', {
            commentAuthor: comment.user_id,
            liker: userId,
            shouldNotify: comment.user_id !== userId
          });
          
          if (comment.user_id !== userId) {
            console.log('🔔 Creating comment like notification for:', comment.user_id);
            const currentUser = await pb.collection('users').getOne(userId);
            
            await pb.collection('notifications').create({
              userId: comment.user_id,
              type: 'comment_like',
              title: 'New like on your comment',
              message: `${currentUser.name || 'Someone'} liked your comment`,
              isRead: false,
              createdAt: new Date().toISOString(),
              data: {
                commentId: comment.id
              }
            });
          }
        } catch (notifError: any) {
          console.error('❌ Failed to create notification:', notifError);
        }
      }
    } catch (error) {
      console.error('Error handling comment like:', error);
      toast.error('Failed to update like');
    }
  };

  const handleEdit = async () => {
    if (!editContent.trim()) return;
    
    try {
      await pb.collection('inspire_comments').update(comment.id, {
        content: editContent.trim()
      });
      
      toast.success('Comment updated successfully');
      setIsEditing(false);
      onEdit();
    } catch (error) {
      console.error('Error updating comment:', error);
      toast.error('Failed to update comment');
    }
  };

  const handlePin = async () => {
    try {
      const currentlyPinned = (comment as any).is_pinned;
      
      if (!currentlyPinned) {
        // If we're pinning this comment, unpin all other comments on this post first
        const allComments = await pb.collection('inspire_comments').getFullList({
          filter: `post_id="${comment.post_id}" && is_pinned=true`
        });
        
        for (const c of allComments) {
          await pb.collection('inspire_comments').update(c.id, {
            is_pinned: false
          });
        }
      }
      
      // Toggle the pin status
      await pb.collection('inspire_comments').update(comment.id, {
        is_pinned: !currentlyPinned
      });
      
      toast.success(!currentlyPinned ? 'Comment pinned' : 'Comment unpinned');
      onEdit();
    } catch (error) {
      console.error('Error pinning comment:', error);
      toast.error('Failed to update pin status');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete?')) {
      return;
    }

    try {
      await pb.collection('inspire_comments').delete(comment.id);
      toast.success('Comment deleted successfully');
      onDelete();
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('Failed to delete comment');
    }
  };

  const handleReply = async () => {
    if (!userId) {
      onAuthRequired?.();
      return;
    }

    if (!replyContent.trim()) return;

    setIsSubmittingReply(true);
    try {
      const newReply = await pb.collection('inspire_comments').create({
        post_id: comment.post_id,
        user_id: userId,
        content: replyContent.trim(),
        parent_comment_id: comment.id
      });

      setReplyContent('');
      setShowReplyInput(false);
      setRepliesCount(prev => prev + 1);
      toast.success('Reply added successfully');
      
      // Send notification to parent comment author
      try {
        console.log('🔔 Checking reply notification conditions:', {
          commentAuthor: comment.user_id,
          replier: userId,
          shouldNotify: comment.user_id !== userId
        });
        
        if (comment.user_id !== userId) {
          console.log('🔔 Creating reply notification for:', comment.user_id);
          const currentUser = await pb.collection('users').getOne(userId);
          
          await pb.collection('notifications').create({
            userId: comment.user_id,
            type: 'comment_reply',
            title: 'New reply to your comment',
            message: `${currentUser.name || 'Someone'} replied to your comment`,
            isRead: false,
            createdAt: new Date().toISOString(),
            data: {
              commentId: comment.id,
              parentCommentId: comment.id
            }
          });
        }
      } catch (notifError: any) {
        console.error('❌ Failed to create notification:', notifError);
        console.error('Error details:', {
          message: notifError.message,
          status: notifError.status,
          data: notifError.data,
          response: notifError.response
        });
        const errorMsg = notifError.data?.message || notifError.message || 'Unknown error';
        toast.error('❌ Notification error: ' + errorMsg);
      }
      
      // Show replies and force refresh
      setShowReplies(true);
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Error adding reply:', error);
      toast.error('Failed to add reply');
    } finally {
      setIsSubmittingReply(false);
    }
  };

  const handleCommenterClick = () => {
    if (tutorRecord?.id) {
      navigate(`/tutor/${tutorRecord.id}`);
    }
  };

  const isClickable = !!tutorRecord?.id;
  const canModify = userId && (userId === comment.user_id || pb.authStore.model?.role === 'admin');
  const canPin = userId && postOwnerId && userId === postOwnerId;

  return (
    <div className={`group flex gap-2.5 ${level > 0 ? 'ml-10 mt-2' : ''}`}>
      <Avatar 
        className={`h-10 w-10 flex-shrink-0 ${isClickable ? 'cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all' : ''}`}
        onClick={isClickable ? handleCommenterClick : undefined}
      >
        {avatarUrl && <AvatarImage src={avatarUrl} alt={commenter?.name || 'User'} />}
        <AvatarFallback className="bg-primary/10 text-primary font-medium">
          {commenter?.name?.charAt(0) || 'U'}
        </AvatarFallback>
      </Avatar>
      
      <div className="flex-1 min-w-0">
        {/* Comment Bubble - full width to prevent shifting */}
        <div className="w-full">
          <div className="bg-muted/60 hover:bg-muted/80 transition-colors rounded-2xl px-4 py-2.5 shadow-sm">
            <div className="flex items-start justify-between gap-2 mb-1">
              <div className="flex items-center gap-2 flex-wrap min-w-0">
                <p 
                  className={`font-semibold text-sm truncate ${isClickable ? 'cursor-pointer hover:underline' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isClickable) handleCommenterClick();
                  }}
                >
                  {commenter?.name || 'User'}
                </p>
                {(comment as any).is_pinned && (
                  <Badge variant="secondary" className="text-xs px-1.5 py-0.5 h-5 bg-primary/10">
                    <Pin className="h-3 w-3 mr-0.5" />
                    Pinned
                  </Badge>
                )}
              </div>
              
              {/* Edit/Delete/Pin buttons - fixed width to prevent layout shift */}
              <div 
                className="flex items-center gap-0.5 flex-shrink-0 w-[90px] justify-end"
                onClick={(e) => e.stopPropagation()}
              >
                {canPin && !isEditing && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handlePin}
                    className={`h-7 w-7 p-0 hover:bg-background/80 ${(comment as any).is_pinned ? 'text-primary' : ''}`}
                    title={(comment as any).is_pinned ? 'Unpin comment' : 'Pin comment'}
                  >
                    <Pin className="h-3.5 w-3.5" />
                  </Button>
                )}
                {canModify && !isEditing && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsEditing(true)}
                      className="h-7 w-7 p-0 hover:bg-background/80"
                    >
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleDelete}
                      className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </>
                )}
              </div>
            </div>
            
            {isEditing ? (
              <div 
                className="space-y-2 mt-2"
                onClick={(e) => e.stopPropagation()}
              >
                <Textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="min-h-[60px] text-sm bg-background"
                  dir="auto"
                  autoFocus
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={handleEdit}
                    disabled={!editContent.trim()}
                    className="h-7"
                  >
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setIsEditing(false);
                      setEditContent(comment.content);
                    }}
                    className="h-7"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div 
                className={`text-sm leading-relaxed break-words ${level === 0 ? 'cursor-pointer' : ''}`}
                dir={detectTextDirection(comment.content)}
                dangerouslySetInnerHTML={{ __html: linkifyWithBreaks(comment.content) }}
                onClick={() => {
                  if (level === 0) {
                    toggleReplies();
                  }
                }}
              />
            )}
          </div>
          
          {/* Timestamp and Actions */}
          <div className="flex items-center gap-3 px-3 mt-1">
            <span className="text-xs text-muted-foreground">
              {formatDistanceToNow(new Date(comment.created), { addSuffix: true })}
            </span>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLike}
              className={`h-auto p-0 text-xs font-semibold hover:bg-transparent ${
                isLiked ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Like{likesCount > 0 && ` · ${likesCount}`}
            </Button>
            
            {level === 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowReplyInput(!showReplyInput)}
                className="h-auto p-0 text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-transparent"
              >
                Reply
              </Button>
            )}
            
            {level === 0 && (repliesCount > 0 || isLoadingReplies) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleReplies();
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  toggleReplies();
                }}
                className="h-auto p-0 text-xs font-semibold text-primary hover:text-primary/80 hover:bg-transparent flex items-center gap-1 touch-manipulation"
              >
                {isLoadingReplies ? (
                  <>Loading replies...</>
                ) : showReplies ? (
                  <>
                    <ChevronUp className="h-3 w-3" />
                    Hide {repliesCount} {repliesCount === 1 ? 'reply' : 'replies'}
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-3 w-3" />
                    View {repliesCount} {repliesCount === 1 ? 'reply' : 'replies'}
                  </>
                )}
              </Button>
            )}
            
            {/* Like indicator */}
            {likesCount > 0 && (
              <div className="ml-auto flex items-center gap-1 text-xs text-muted-foreground">
                <Heart className={`h-3 w-3 ${isLiked ? 'fill-primary text-primary' : ''}`} />
                {likesCount}
              </div>
            )}
          </div>
        </div>

        {/* Reply Input */}
        {showReplyInput && (
          <div className="flex gap-2 mt-3 animate-fade-in">
            <Avatar className="h-8 w-8 flex-shrink-0">
              <AvatarFallback className="bg-primary/10 text-primary text-xs">
                {commenter?.name?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 flex gap-2">
              <Textarea
                placeholder="Write a reply..."
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                className="min-h-[60px] text-sm resize-none rounded-2xl"
                dir="auto"
                autoFocus
              />
              <div className="flex flex-col gap-2">
                <Button
                  size="sm"
                  onClick={handleReply}
                  disabled={!replyContent.trim() || isSubmittingReply}
                  className="h-8 w-8 p-0"
                  title="Send reply"
                >
                  <Reply className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setShowReplyInput(false);
                    setReplyContent('');
                  }}
                  className="h-8 w-8 p-0"
                  title="Cancel"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Nested Replies */}
        {level === 0 && showReplies && (
          <div ref={repliesContainerRef} className="mt-3 space-y-1 animate-fade-in border-l-2 border-border/50 pl-2">
            {isLoadingReplies ? (
              <div className="text-sm text-muted-foreground py-2">Loading replies...</div>
            ) : replies.length > 0 ? (
              replies.map(reply => (
                <CommentItem
                  key={reply.id}
                  comment={reply}
                  userId={userId}
                  postOwnerId={postOwnerId}
                  onDelete={() => {
                    setRepliesCount(prev => Math.max(0, prev - 1));
                    setRefreshKey(prev => prev + 1);
                  }}
                  onEdit={() => setRefreshKey(prev => prev + 1)}
                  onAuthRequired={onAuthRequired}
                  level={1}
                />
              ))
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};
